"use client";

import { useState, useRef, useEffect } from "react";
import { CAMPAIGN_CATEGORIES, type CategoryId } from "@/lib/categories";

interface CategoryMultiSelectProps {
  selected: CategoryId[];
  onChange: (categories: CategoryId[]) => void;
  max?: number;
}

export function CategoryMultiSelect({
  selected,
  onChange,
  max = 3,
}: CategoryMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (id: CategoryId) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else if (selected.length < max) {
      onChange([...selected, id]);
    }
  };

  const handleRemove = (id: CategoryId) => {
    onChange(selected.filter((s) => s !== id));
  };

  const selectedCategories = selected
    .map((id) => CAMPAIGN_CATEGORIES.find((c) => c.id === id))
    .filter(Boolean);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="mt-1 flex w-full min-h-[42px] items-center flex-wrap gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        {selectedCategories.length > 0 ? (
          selectedCategories.map((cat) => (
            <span
              key={cat!.id}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-0.5 text-xs font-medium text-blue-700"
            >
              <span>{cat!.icon}</span>
              <span>{cat!.name}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(cat!.id);
                }}
                className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 transition-colors"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))
        ) : (
          <span className="text-gray-400">Select categories...</span>
        )}
        <svg
          className={`ml-auto w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-60 overflow-auto">
          {CAMPAIGN_CATEGORIES.map((category) => {
            const isSelected = selected.includes(category.id);
            const isDisabled = !isSelected && selected.length >= max;

            return (
              <button
                key={category.id}
                type="button"
                disabled={isDisabled}
                onClick={() => handleSelect(category.id)}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors
                  ${isSelected ? "bg-blue-50 text-blue-700" : isDisabled ? "text-gray-300 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50"}
                `}
              >
                <span className="text-base">{category.icon}</span>
                <span className="flex-1">{category.name}</span>
                {isSelected && (
                  <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
